import type { MissionType } from '@/lib/mission-type';

/**
 * What the product calls a mission, its kinds, and whether a station is manned.
 *
 * CASE. The type labels render inside `uppercase` classes on the compose picker and the COP
 * mission rows, so sentence case here is unchanged on screen. They ALSO appear mid-sentence
 * ("A HOVER / LOITER takes one point"), where the capitals came from the string and read as
 * shouting inside a sentence - one more reason the words belong here rather than inline.
 */

/**
 * The six mission kinds, named by what the drone does.
 *
 * Was `HOVER / LOITER`, `GO TO POINT`, `OUT & BACK`, `FOLLOW ROUTE`, `PATROL / LOOP`, `ORBIT`.
 * Two of them offered the operator a slash and two words for one thing, and "loiter" is aviation
 * register for hovering.
 */
export const MISSION_TYPE_LABEL: Readonly<Record<MissionType, string>> = {
  hover: 'Hover',
  goto: 'Fly to a point',
  out_and_back: 'Out and back',
  route: 'Follow a route',
  /** Was `PATROL / LOOP`. What it does is fly the same route again and again. */
  patrol: 'Repeating route',
  /** Was `ORBIT`. A circle around a point, which is what the label now says. */
  orbit: 'Circle a point',
};

/**
 * The type named so it can sit mid-sentence after "a" or "an".
 *
 * `missions/[id]/page.tsx` and `CopMissionEdit.tsx` build sentences like
 * "A ${label} takes one point", which rendered "A OUT & BACK takes one point" - a wrong article
 * AND a shout inside a sentence. Lower-casing the label and choosing the article from its first
 * letter fixes both, and keeps the fix in one place rather than at four call sites.
 */
export function aMissionType(
  type: MissionType,
  { capitalised = false }: { capitalised?: boolean } = {},
): string {
  const label = MISSION_TYPE_LABEL[type].toLowerCase();
  const article = /^[aeiou]/.test(label) ? 'an' : 'a';
  return `${capitalised ? article[0].toUpperCase() + article.slice(1) : article} ${label}`;
}

/** One line each, in the operator's terms - what the drone actually does. */
export const MISSION_TYPE_DESCRIPTION: Readonly<Record<MissionType, string>> = {
  // #373: a hover may be one point or a whole route. The sentence has to be true of both, and
  // has to name WHERE the hold happens - "remains hovering there" left an operator who placed
  // three points with no way to know which one the drone stops over.
  hover: 'Flies its waypoints in order and hovers at the last one',
  goto: 'Flies to a point you choose',
  out_and_back: 'Flies to a point or area, then returns to where it launched',
  route: 'Flies through a set of waypoints in order',
  patrol: 'Flies the same route again and again',
  orbit: 'Circles a point at a set radius',
};

/**
 * Types with no flight path yet, and why.
 *
 * Shown at PLAN time as a warning, not a block. An operator may legitimately want the mission on
 * the board before the capability exists; what they must not get is a launch control that looks
 * armed and then refuses.
 */
export const MISSION_TYPE_UNFLYABLE: Readonly<Partial<Record<MissionType, string>>> = {
  patrol:
    'A repeating route cannot be flown yet. This mission can be planned and briefed, but not '
    + 'launched',
};

/**
 * Why an ORBIT cannot be launched from a DOCK, in the operator's words.
 *
 * Not in the table above, and the distinction is the point: an orbit CAN be flown now. What it
 * cannot do is leave a dock-stationed aircraft, because a circle never finishes, so the
 * mission-finished ack that starts the automatic dock return never comes and nothing would bring
 * the aircraft home. Mirrors `dockedOrbitRefusal` in `src/services/mission-launch.ts`; the server
 * is the authority and this is what the surface says BEFORE an operator arms a launch it would
 * refuse.
 */
export const ORBIT_DOCK_UNLAUNCHABLE =
  'Pick an aircraft that is not stationed in a dock, or a different mission type. A circle never '
  + 'ends, so nothing would bring this one back to its dock';

/**
 * WHAT AN ORBIT WILL ACTUALLY DO, at the moment of commitment (#700 review HIGH).
 *
 * The defining property of this type is that it does NOT end, and before this the only place
 * that was ever said was inside a REFUSAL (`ORBIT_DOCK_UNLAUNCHABLE`). On the path that is
 * allowed - a hand-launched companion aircraft - the operator saw "Circles a point at a set
 * radius", a green "Aircraft and route ready", and then "Launched, 1 waypoint commanded": the
 * same words a GO TO POINT produces, for a sortie that will never finish on its own.
 *
 * Nothing said that no mission-finished ack is ever coming, or that the only thing which ends it
 * is their own STOP. The one type where "what happens at the end" IS the safety case was the one
 * type that never mentioned it.
 *
 * Takes the numbers because a bare "it will not end" is unactionable at 02:00 - the operator
 * needs to recognise the circle they just drew. Ends on who owns the recall, because that is the
 * part they have to act on.
 */
export function orbitLaunchWarning(
  orbit: { radiusM: number; direction: 'cw' | 'ccw' } | null,
): string {
  const shape = orbit
    ? `circle r ${orbit.radiusM} m ${orbit.direction === 'ccw' ? 'CCW' : 'CW'}`
    : 'circle';
  return `This ORBIT does not end. The aircraft flies the ${shape} until you STOP it - `
    + 'no mission-finished ack is coming, so nothing brings it home on its own.';
}

/**
 * The launched-orbit toast headline, which must not report a waypoint count.
 *
 * An orbit uploads no wayline: `plural(waypoints.length, ...)` rendered "Launched, 1 waypoint
 * commanded", which is both untrue about what went on the wire and indistinguishable from the
 * GO TO POINT the operator did not launch.
 */
export const ORBIT_LAUNCHED_HEADLINE = 'Launched - circling until you stop it';

/**
 * Why an ORBIT cannot be flown by this AIRFRAME at all.
 *
 * The other half of the same question. An orbit reaches the aircraft as `start_orbit` over the
 * companion app on the RC; a docked Matrice (DJI Cloud), a SkyPulse (MAVLink) and a Skydio have
 * no circular primitive at the end of that wire. Mirrors the `command_unsupported` refusal in
 * `src/services/mission-launch.ts`.
 *
 * Both strings lead with WHAT TO DO. The mechanism follows, because an operator reading this at
 * 02:00 needs the next action first and the protocol reason second, if at all.
 */
export const ORBIT_AIRFRAME_UNSUPPORTED =
  'Pick a DJI companion aircraft, or a different mission type. This airframe has no circular '
  + 'flight path';

/**
 * The short reason a PICKER gives for a type it will not offer at all.
 *
 * Deliberately separate from `MISSION_TYPE_UNFLYABLE`, which is the reason a mission that ALREADY
 * EXISTS cannot be launched - a sentence an operator reads on a mission already on the board,
 * where "planned and briefed but not launched" is exactly true. On a picker it would not be:
 * compose no longer lets these types be chosen, so promising the operator they can plan one
 * would be a lie told by the control that just refused them.
 *
 * SHOVRON is named as the limit, NOT the drone. An earlier wording said "the aircraft cannot be
 * told to repeat a route", which an operator knows to be false - an M350 flies repeating waypoint
 * missions, and DJI's POI mode flies a circle at a set radius. Told that, an operator concludes
 * either that we are wrong about their airframe or that THIS one is faulty. The gap is in our
 * mission-execution layer.
 */
export const MISSION_TYPE_UNAVAILABLE: Readonly<Partial<Record<MissionType, string>>> = {
  patrol: 'Not available yet. SHOVRON cannot fly a repeating route',
};

/** The fallback when a type is refused but has no wording of its own. */
export const MISSION_TYPE_UNAVAILABLE_FALLBACK = 'Not available yet. SHOVRON cannot fly this mission';

/**
 * Whether a station is manned right now, on the COP's mission rows.
 *
 * Was `COVERED` / `GAP` / `HANDOVER`. "Gap" is the wire's word for an empty station and says
 * nothing about what is wrong; "handover" is the word for the manoeuvre, not for what an
 * operator is looking at. Only these three states earn a chip - the rest are configuration
 * facts, and the mission planner is where those are read.
 */
export const COVERAGE_ROW_LABEL = {
  covered: 'Covered',
  /**
   * The direct negation of "Covered", which is the only other thing this column ever says.
   *
   * Was `GAP` - the wire's noun for an empty station, which says nothing about what is wrong.
   * "Nobody on station" was tried and the guard test rejected it: "on station" is itself a
   * retired phrase, and a column that reads "Covered" one row and "Nobody on station" the next
   * makes an operator work out that they are opposites.
   */
  gap: 'Not covered',
  /** One drone is taking over from another. */
  handover: 'Changing over',
} as const;

/**
 * What the product calls the log of missions already flown or cancelled (`/missions/history`).
 *
 * It used to be a row in the left rail, which is why the name was in `NAV_ITEM_LABEL`. #505's
 * evaluator read thirteen rows and could not find the one action he wanted, so the record of
 * finished work left the rail and is now reached from the missions board it belongs under. The
 * name came with it rather than staying behind: that map is keyed by href precisely so every
 * entry in it is a row in the rail, and an entry that is not one makes the key a lie.
 *
 * It does not go inline at the call sites either. Three surfaces name this page - the button on
 * the board, the page's own title, and the way back from a briefing opened out of it - and with
 * no rail row left to anchor them, three literals is exactly how they drift apart.
 *
 * The shape mirrors `AIR_PICTURE_TITLE` in `nav.ts`: a page's name is one string, not a map.
 */
export const PAST_MISSIONS_TITLE = 'Past missions';

/** Fallbacks where a mission row has no drone to name. Were `NO A/C` and `Its aircraft`. */
export const MISSION_ROW_FALLBACK = {
  noDrone: 'No drone',
  itsDrone: 'Its drone',
} as const;

/**
 * Flying a finished plan again.
 *
 * "Fly again", not "relaunch" or "re-fly". Two reasons, and the second is the load-bearing
 * one. Plainly, an operator says they want to fly it again. Structurally, "relaunch" is
 * already taken: in this product it names the continuous-coverage rotation putting the SAME
 * running mission onto the next drone, which is a different act with a different result, and
 * two unrelated things reading alike in a mission list is how an operator presses the wrong
 * one.
 *
 * `done` says "a new plan" rather than staying silent, because the press navigates the
 * operator to a DIFFERENT mission from the one they were reading. Without that sentence the
 * page changes under them and the only clue is the name in the title.
 *
 * THERE ARE TWO DONE MESSAGES because the dialog asks one question and the answers produce
 * different plans. Telling an operator who booked 0600 that their plan is "ready to fly" is
 * the wrong sentence, and it is the only sentence they get before the page changes.
 *
 * No trailing period on `label`, `busy`, `confirm` or `failed`: they are labels, not sentences.
 */
export const MISSION_COPY_ACTION = {
  label: 'Fly again',
  /** The icon-only control in the Past missions row needs its meaning spelled out. */
  hint: 'Copy this plan into a new mission',
  /** The dialog's own sentence. Names the plan, so there is no doubt which one is being copied. */
  explain: (missionName: string) =>
    `This copies ${missionName} into a new mission with the same route. The finished one is left as it is.`,
  whenReady: 'Launch it when ready',
  atTime: 'Set a departure time',
  confirm: 'Create the plan',
  busy: 'Copying',
  done: 'Copied. This is a new plan, ready to fly',
  doneScheduled: 'Copied. The new plan is booked for the time you set',
  failed: 'Could not copy this mission',
} as const;
